class MenuDrawer extends SideDrawer {
  constructor() {
    super();
    this.moved = false;
    this.header = this.header || document.querySelector('.header-wrapper');
    this.isOpen = false;
    this.resizeFrame = null;
    this.headerBottom = null;
    this.onBodyClickEvent = this.onBodyClick.bind(this);
  }

  open(triggeredBy) {
    if (this.isOpen) return;
    this.isOpen = true;

    if (this.header) {
      this.header.classList.add('open-menu-drawer');
      this.updateHeaderPosition();
    }
    document.body.addEventListener('click', this.onBodyClickEvent);
    window.addEventListener('resize', this.onResize);
    super.open(triggeredBy);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    super.close();
    if (this.header) {
      this.header.classList.remove('open-menu-drawer');
      document.documentElement.style.removeProperty('--header-bottom-position');
    }
    if (this.resizeFrame !== null) {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }
    this.headerBottom = null;
    document.body.removeEventListener('click', this.onBodyClickEvent);
    window.removeEventListener('resize', this.onResize);
  }

  onBodyClick(event) {
    if (!this.activeElement?.contains(event.target) && !this.contains(event.target)) {
      this.close();
    }
  }

  updateHeaderPosition() {
    if (!this.header) return;

    const headerBottom = Math.trunc(this.header.getBoundingClientRect().bottom);
    if (headerBottom === this.headerBottom) return;

    this.headerBottom = headerBottom;
    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);
  }

  onResize = () => {
    if (this.resizeFrame !== null) return;

    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.updateHeaderPosition();
    });
  }
}

customElements.define('menu-drawer', MenuDrawer);

if (!customElements.get('menu-details')) {
  customElements.define('menu-details',
    class MenuDetails extends HTMLElement {
      constructor() {
        super();
        this.openTimer = null;
        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.onButtonClick = this.onButtonClick.bind(this);
      }

      connectedCallback() {
        this._parent = this.closest('[data-menu-details-parent]');
        this._details = this.querySelector(':scope > details');
        this._summary = this._details?.querySelector(':scope > summary');
        this._button = this._details?.querySelector('button');
        this._megaMenu = this.querySelector('mega-menu');

        this._summary?.addEventListener('click', this.onSummaryClick);
        this._button?.addEventListener('click', this.onButtonClick);
      }

      disconnectedCallback() {
        clearTimeout(this.openTimer);
        this.openTimer = null;
        this._summary?.removeEventListener('click', this.onSummaryClick);
        this._button?.removeEventListener('click', this.onButtonClick);
      }

      get parent() {
        return this._parent || this.closest('[data-menu-details-parent]');
      }

      get details() {
        return this._details || this.querySelector(':scope > details');
      }

      get summary() {
        return this._summary || this.details?.querySelector(':scope > summary');
      }

      get button() {
        return this._button || this.details?.querySelector('button');
      }

      setMegaMenuOpen() {
        this._megaMenu?.setAttribute('open', true);
      }
  
      setMegaMenuClose() {
        this._megaMenu?.removeAttribute('open');
      }

      onSummaryClick() {
        this.setMegaMenuOpen();
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => {
          this.openTimer = null;
          if (!this.isConnected) return;

          // Level 1 list is both the scroll container and the containing block of the
          // absolute submenu, so they share scrollTop. Save it and reset to 0 so the
          // submenu starts at the top; it's restored on back.
          const parent = this.parent;
          if (parent) {
            this.parentScrollTop = parent.scrollTop;
            parent.style.scrollBehavior = 'auto';
            parent.scrollTop = 0;
          }

          parent?.classList.add('active');
          this.classList.add('active');
          this.setAttribute('open', true);
          this.details?.setAttribute('open', true);
          this.summary?.setAttribute('aria-expanded', true);
        }, 100);
      }

      onButtonClick() {
        clearTimeout(this.openTimer);
        this.openTimer = null;
        this.setMegaMenuClose();
        const parent = this.parent;
        parent?.classList.remove('active');
        this.classList.remove('active');
        this.removeAttribute('open');
        this.details?.removeAttribute('open');
        this.summary?.setAttribute('aria-expanded', false);

        const submenuScroll = this.querySelector('.menu-drawer__submenu-scroll');
        if (submenuScroll) submenuScroll.scrollTop = 0;
        if (parent) {
          parent.style.scrollBehavior = 'auto';
          parent.scrollTop = this.parentScrollTop || 0;
          parent.style.scrollBehavior = '';
        }
      }
    }
  )
}

if (!customElements.get('menu-disclosure')) {
  customElements.define('menu-disclosure',
    class MenuDisclosure extends HTMLElement {
      constructor() {
        super();
        this._open = false;
        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
      }

      get open() {
        return this._open
      }

      set open(value) {
        value = Boolean(value);
        if (value === this._open) return;

        this._open = value;
        if (value) {
          this.details.setAttribute('open', '');
          this.classList.add('active');
          this.closeOtherDisclosures();
        } else {
          this.details.removeAttribute('open');
          this.classList.remove('active');
        }

        this.summaryElement?.setAttribute('aria-expanded', value ? 'true' : 'false');
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this } }));
      }

      get details() {
        return this._details || this.querySelector(':scope > details');
      }

      get summaryElement() {
        return this._summaryElement || this.details?.querySelector(':scope > summary');
      }

      get contentElement() {
        return this._contentElement || this.details?.querySelector(':scope > summary + *');
      }

      get disclosureGroup() {
        return this._disclosureGroup || this.closest('ul, ol, [role="list"]');
      }

      connectedCallback() {
        this._details = this.querySelector(':scope > details');
        if (!this._details) return;

        this._summaryElement = this._details.querySelector(':scope > summary');
        this._contentElement = this._details.querySelector(':scope > summary + *');
        this._disclosureGroup = this.closest('ul, ol, [role="list"]');
        this._open = this._details.hasAttribute('open');

        this.summaryElement?.addEventListener('click', this.onSummaryClick);
        this.details.addEventListener('keyup', this.onKeyUp);
        this.classList.toggle('active', this.open);
        this.summaryElement?.setAttribute('aria-expanded', this.open ? 'true' : 'false');
        if (this.open) this.closeOtherDisclosures();
      }

      disconnectedCallback() {
        this.summaryElement?.removeEventListener('click', this.onSummaryClick);
        this.details?.removeEventListener('keyup', this.onKeyUp);
      }

      onSummaryClick(event) {
        event.preventDefault();
        this.toggle();
      }

      toggle(forceState = !this.open) {
        this.open = forceState;
      }

      openDetails() {
        this.open = true;
      }

      closeDetails() {
        this.querySelectorAll('menu-disclosure.active').forEach(disclosure => disclosure.closeDetails());
        this.open = false;
      }

      closeOtherDisclosures() {
        const group = this.disclosureGroup;
        if (!group) return;

        group.querySelectorAll(':scope > li > menu-disclosure.active, :scope > menu-disclosure.active').forEach(disclosure => {
          if (disclosure !== this) disclosure.closeDetails();
        });
      }

      onKeyUp(event) {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.closeDetails();
        }
      }
    }
  )
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-menu-drawer-localization]');
  if (!trigger) return;

  const opener = document.querySelector('.announcement-bar__language-opener');
  const modalSelector = opener?.getAttribute('data-modal');
  const modal = modalSelector ? document.querySelector(modalSelector) : null;
  if (!modal || typeof modal.show !== 'function') return;

  event.preventDefault();
  modal.show(trigger);
});
